import { NavSection } from "./SelectionPanel";
import { ConfigTarget, actionNeedsSetup, isAuth, isSectionWorthy, isDormantAction, isFlowAction, missingRequiredIn, slugOf } from "./actionSetup";
import { isExemptAction } from "./tempExemptions";

type Translate = (key: any, options?: any) => string;

export const DETAILS_SLUG = "details";

// What a section is called: the connector's own wording wins — an explicit
// ui:title, else the action name. Never schema.title, which is the Python config
// class name. Only a nameless auth action falls back to a generic label.
export const actionLabel = (a: any, t: Translate) =>
    a?.ui_schema?.["ui:title"] || a?.name || (isAuth(a) ? t("Credentials") : slugOf(a));

// The nav every configuration screen shows: the integration's own fields first,
// then its actions grouped the same way the flow map's node panel groups them —
// credentials, then the directions data can move, then anything else.
export const ADVANCED_CAT = "Advanced Config";

// Whether a flow action's required fields count as work owed right now: not a
// direction no enabled arrow carries (dormant), and not a GUNDI-5680 exempt
// section. The nav "!" badge and the form's per-field hints share this so they
// can't disagree.
export const flowActionCounts = (a: any, dormantTypes?: Set<string>, exemptSlugs?: Set<string>) =>
    isFlowAction(a) && !isDormantAction(a, dormantTypes) && !isExemptAction(a, exemptSlugs);

export function buildActionNav(
    { target, actions, t, detailsLabel, detailsSlug, detailsNeedsSetup, detailsRequiredCount, dormantTypes, authBadgesOnly, exemptSlugs }: {
        // Undefined before the record exists (create flows) — no "!" badges then,
        // since nothing could have been saved yet.
        target?: ConfigTarget;
        actions: any[];
        t: Translate;
        detailsLabel?: string;
        // The flow map's account panel calls its first section "general"; every
        // other screen uses DETAILS_SLUG.
        detailsSlug?: string;
        detailsNeedsSetup?: boolean;
        // How many of the details section's own required fields are still empty
        // (name, workspace, address). It has no action behind it, so the count
        // has to be handed in — without it the one section every Route needs was
        // the only one carrying no count.
        detailsRequiredCount?: number;
        // Flow-action types ("pull"/"push") whose direction isn't carrying data
        // right now. Their sections stay editable as FYIs but carry no "!" badge —
        // nothing blocks the flow without them. (No "Optional" chip either: plenty
        // of other sections are optional too and aren't labeled.)
        dormantTypes?: Set<string>;
        // Whether a non-auth field is really required depends on the role the
        // Route gives this account (provider vs. destination) — a role the
        // standalone Connection page doesn't know. Only credentials are required
        // regardless, so that page badges auth actions alone; the flow map,
        // which knows the role, keeps badging flow actions too.
        authBadgesOnly?: boolean;
        // GUNDI-5680 temporary hack: action slugs that never badge on this account
        // (see tempExemptions.ts). Same set computeNeedsSetup uses for the node.
        exemptSlugs?: Set<string>;
    },
): NavSection[] {
    // Advanced Config helpers (non-flow, non-auth) never badge: their required
    // schema fields serve an on-demand action, and nothing in the flow waits on
    // them. Same call computeNeedsSetup makes for the node badge.
    // Credentials badge unconditionally — the exemption can only ever quiet a
    // flow action, never a missing token.
    const canBadge = (a: any) => isAuth(a)
        || (!authBadgesOnly && flowActionCounts(a, dormantTypes, exemptSlugs));
    const toItem = (a: any) => ({
        slug: slugOf(a),
        label: actionLabel(a, t),
        needsSetup: !!target && canBadge(a) && actionNeedsSetup(target, a),
    });
    // A section with nothing to fill in and nothing to run isn't a section
    // (GUNDI-5697). One filter here covers every screen the nav drives: the
    // creation wizards, the Connection page, and the flow map's node panel.
    const all = (actions || []).filter(isSectionWorthy);
    const groups: [string, any[]][] = [
        [t("Credentials"), all.filter(isAuth)],
        [t("Send data to Gundi"), all.filter((a) => !isAuth(a) && a.type === "pull")],
        [t("Receive data from Gundi"), all.filter((a) => !isAuth(a) && a.type === "push")],
        [t(ADVANCED_CAT), all.filter((a) => !isAuth(a) && a.type !== "pull" && a.type !== "push")],
    ];
    const nav: NavSection[] = [{
        cat: t("GENERAL"),
        items: [{
            slug: detailsSlug || DETAILS_SLUG,
            label: detailsLabel || t("General"),
            needsSetup: !!detailsNeedsSetup,
            requiredCount: detailsRequiredCount,
        }],
    }];
    groups.forEach(([cat, group]) => {
        if (group.length > 0) nav.push({ cat, items: group.map(toItem) });
    });
    return nav;
}

export const actionForSlug = (actions: any[], slug?: string) =>
    (actions || []).find((a) => slugOf(a) === slug);

// Marks the sections whose action has field errors from the last failed submit.
// The creation screens (New Connection, new-Route wizard) deliberately do NOT
// relocate the user, so this badge plus the toast is the whole signal that
// something is wrong in a section they may never have opened — which is why both
// screens read it from here instead of keeping their own copy.
export function flagSectionErrors(
    nav: NavSection[],
    actions: any[],
    // action id -> { field: message }
    fieldErrors: Record<string, Record<string, string>> | undefined,
): NavSection[] {
    const hasErrors = (a: any) => Object.keys(fieldErrors?.[a?.id] ?? {}).length > 0;
    return nav.map((section) => ({
        ...section,
        items: section.items.map((item) => {
            const action = (actions || []).find((a) => slugOf(a) === item.slug);
            return action && hasErrors(action) ? { ...item, needsSetup: true } : item;
        }),
    }));
}

// Route creation only (product call). There, a section shows a COUNT of the
// required fields it is still missing instead of the "!" the other screens use:
// nothing has failed yet — the record does not even exist — so the nav reports
// what a section needs rather than accusing it of being broken. The count is
// grey until a save actually fails, then the SAME chip goes red, so a section
// never carries two marks meaning the same thing.
//
// A section with no required fields gets no chip at all: the absence is what
// says "this is here if you want it, and nothing is owed".
export function attachRequiredCounts(
    nav: NavSection[],
    actions: any[],
    // The draft values for an action — creation keeps them in local state.
    dataFor: (action: any) => any,
    // action id -> { field: message } from the last failed submit.
    fieldErrors: Record<string, Record<string, string>> | undefined,
): NavSection[] {
    return nav.map((section) => ({
        ...section,
        items: section.items.map((item) => {
            const action = (actions || []).find((a) => slugOf(a) === item.slug);
            // The details section: its count is handed to buildActionNav, and a
            // failed submit turns that same chip red rather than adding a "!"
            // beside it — the rule every other section follows here.
            if (!action) return { ...item, needsSetup: false, requiredIsError: !!item.needsSetup };
            // needsSetup cleared for EVERY action section, not just the ones
            // that end up with a chip. Clearing it only where a chip appeared
            // meant that filling the last required field swapped the chip for
            // the "!" flagSectionErrors had left behind — the mark changed shape
            // at the exact moment the user fixed the problem.
            return {
                ...item,
                needsSetup: false,
                requiredCount: missingRequiredIn(action, dataFor(action)).length,
                requiredIsError: Object.keys(fieldErrors?.[action.id] ?? {}).length > 0,
            };
        }),
    }));
}

// How many sections are still missing required fields — what the hint under the
// landing section's fields counts.
export const sectionsMissingRequired = (
    actions: any[],
    dataFor: (action: any) => any,
): number => (actions || []).filter((a) => missingRequiredIn(a, dataFor(a)).length > 0).length;
